import React, { useCallback, useMemo } from 'react'
import { subscribe } from 'react-contextual'
import { Button, FocusMode, Dimmer, ControlledTooltip } from '../../../../../views/components'
import MultiApplicationsTable from '../../MultiApplicationsTable/MultiApplicationsTable'
import { keys, props as _props } from 'ramda'
import { useDispatch, useSelector } from 'react-redux'
import { portfolioApiSelectors } from '../../../../api/portfolio'
import { portfolioEvents, portfolioSelectors } from '../../../state'
import { userApiSelectors } from '../../../../api/user'
import PortfoliosProvider from 'modules/portfolios/PortfoliosProvider'
import { TApplication, TParty, TTenant } from 'modules/api/portfolio/types'
import { NewContactForm } from 'modules/contacts'
import styles from './ApplicationsTable.module.scss'

type Props = {
  portfolioId: string
  applications: TApplication[]
  tenants: TTenant
  redirect(redirectTo: string): void
  addApplicationToLease(partyValues: any, applicationFee: number): void
  portfolioStatus: string
}

const ApplicationsTable = ({
  portfolioId,
  applications,
  tenants,
  redirect,
  addApplicationToLease,
  portfolioStatus,
}: Props) => {
  const dispatch = useDispatch()

  const getIsPortfolioDraft = useSelector(portfolioApiSelectors.isDraftPortfolio)
  const getAcceptedApplication = useSelector(portfolioApiSelectors.getAcceptedApplicationByPortfolioId)
  const isReadOnly = useSelector(userApiSelectors.isReadOnlyRole)

  const isNewPartyModalOpen = useSelector(portfolioSelectors.isNewPartyModalOpen)
  const isPortfolioDraft = getIsPortfolioDraft(portfolioId)

  // Accepting an applicant is what adds the tenant to the lease, so once the vetting process
  // has started the wizard can't move on to the Tenants step until one is accepted.
  const mustAcceptApplication = applications.length > 0 && !getAcceptedApplication(portfolioId)

  const onSubmit = useCallback((values: TParty, applicationFee) => {
    const personOrCompany = keys(values)[0]
    const partyValues = {
      [personOrCompany]: values[personOrCompany],
    }
    addApplicationToLease(partyValues, applicationFee)
  }, [])

  const WrappedNewContactForm = useMemo(
    () =>
      function PartiesTableContactForm() {
        return (
          <NewContactForm
            onSubmit={(values: TParty) => onSubmit(values, 0)}
            allowSearchable
            getButtonText={(values: TParty) =>
              values?.id ? `Select contact and add application to lease` : `Create contact and add application to lease`
            }
          />
        )
      },
    [],
  )

  return (
    <>
      <div className={styles.root}>
        <Dimmer active={isReadOnly} bgColor="white">
          <MultiApplicationsTable
            applicationDetails={applications}
            portfolioId={portfolioId}
            onEdit={partyId => redirect(`/contacts/${partyId}/edit`)}
            allowEdit={portfolioStatus !== 'terminated'}
            tenants={tenants}
          />
          <div className={styles.footer}>
            {isPortfolioDraft && (
              <>
                <Button onClick={() => redirect(`/leases/${portfolioId}/edit/property/owner`)}>Back</Button>
                {mustAcceptApplication ? (
                  <ControlledTooltip
                    body="To proceed accept an applicant to add as the tenant to the lease."
                    place="above"
                  >
                    <span className={styles['next-disabled']}>
                      <Button disabled>Next</Button>
                    </span>
                  </ControlledTooltip>
                ) : (
                  <Button onClick={() => redirect(`/leases/${portfolioId}/edit/tenants`)}>
                    {applications.length === 0 ? 'Skip' : 'Next'}
                  </Button>
                )}
              </>
            )}
          </div>
        </Dimmer>
      </div>
      {isNewPartyModalOpen && (
        <FocusMode
          isOpen={isNewPartyModalOpen}
          title="New application"
          ContentComponent={WrappedNewContactForm}
          onClose={() => dispatch(portfolioEvents.togglePartyModal())}
        />
      )}
    </>
  )
}

export default subscribe([PortfoliosProvider], ({ tenants, addApplicationToLease, applications }) => ({
  tenants,
  addApplicationToLease,
  applications,
}))(ApplicationsTable)
